import { Fragment, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography
} from "@mui/material";

import Iconify from "../../minimals/components/iconify";
import { AppRouteObject } from "../routes/types";
import { buildOrderNumberRoutePath, routeRequiresOrderNumber } from "../routes/orderNumberRouting";
import { extractDirectoryRoutes } from "../routes/utils";
import OrderNumberRouteDialog from "./OrderNumberRouteDialog";

type CommandPaletteRoute = {
  title: string;
  path: string;
  requiresOrderNumber: boolean;
  orderNumberQueryParam?: string;
};

const normalize = (value: string) => value.toLowerCase().trim();

const getRouteScore = (route: CommandPaletteRoute, query: string) => {
  if (!query) {
    return 1;
  }

  const title = normalize(route.title);
  const path = normalize(route.path);

  if (title === query || path === query) {
    return 100;
  }

  if (title.startsWith(query)) {
    return 80;
  }

  if (path.startsWith(query)) {
    return 70;
  }

  if (title.includes(query)) {
    return 50;
  }

  if (path.includes(query)) {
    return 40;
  }

  return 0;
};

const HighlightedText = ({ value, query }: { value: string; query: string }) => {
  const normalizedValue = value.toLowerCase();
  const normalizedQuery = query.toLowerCase().trim();
  const matchIndex = normalizedQuery ? normalizedValue.indexOf(normalizedQuery) : -1;

  if (matchIndex === -1) {
    return <>{value}</>;
  }

  const before = value.slice(0, matchIndex);
  const match = value.slice(matchIndex, matchIndex + normalizedQuery.length);
  const after = value.slice(matchIndex + normalizedQuery.length);

  return (
    <>
      {before}
      <Box component="mark" sx={{ bgcolor: "transparent", color: "primary.main", fontWeight: 600 }}>
        {match}
      </Box>
      {after}
    </>
  );
};

type CommandPaletteProps = {
  routes: AppRouteObject[];
};

export default function CommandPalette({ routes }: CommandPaletteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const activeRouteRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [routeAwaitingOrderNumber, setRouteAwaitingOrderNumber] = useState<CommandPaletteRoute | null>(null);

  const paletteRoutes = useMemo<CommandPaletteRoute[]>(
    () =>
      extractDirectoryRoutes(routes)
        .filter((route) => route.path && route.title)
        .map((route) => ({
          title: route.title || "",
          path: route.path || "",
          requiresOrderNumber: routeRequiresOrderNumber(route),
          orderNumberQueryParam: route.orderNumberQueryParam
        })),
    [routes]
  );

  const matchingRoutes = useMemo(() => {
    const normalizedQuery = normalize(query);

    return paletteRoutes
      .map((route) => ({
        route,
        score: getRouteScore(route, normalizedQuery)
      }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score || a.route.title.localeCompare(b.route.title))
      .map(({ route }) => route);
  }, [paletteRoutes, query]);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    activeRouteRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelect = (route: CommandPaletteRoute) => {
    if (route.requiresOrderNumber) {
      setRouteAwaitingOrderNumber(route);
      setOpen(false);
      return;
    }

    navigate(route.path);
    setOpen(false);
  };

  const handleOrderNumberDialogClose = () => {
    setRouteAwaitingOrderNumber(null);
  };

  const handleOrderNumberSubmit = (orderNumber: string) => {
    if (!routeAwaitingOrderNumber) {
      return;
    }

    navigate(buildOrderNumberRoutePath(routeAwaitingOrderNumber, orderNumber));
    handleOrderNumberDialogClose();
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, matchingRoutes.length - 1));
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    }

    if (event.key === "Enter" && matchingRoutes[activeIndex]) {
      event.preventDefault();
      handleSelect(matchingRoutes[activeIndex]);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            mt: { xs: 2, sm: 8 },
            borderRadius: 1,
            overflow: "hidden"
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5 }}>
            <TextField
              autoFocus
              fullWidth
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Search pages or URLs"
              variant="standard"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                )
              }}
            />
            <Chip label="Cmd K" size="small" sx={{ display: { xs: "none", sm: "inline-flex" } }} />
          </Box>

          <Divider />

          {matchingRoutes.length === 0 ? (
            <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
              <Typography variant="subtitle2">No matching pages</Typography>
              <Typography variant="body2" color="text.secondary">
                Try a page name or part of the URL.
              </Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ maxHeight: 420, overflowY: "auto", py: 1 }}>
              {matchingRoutes.map((route, index) => (
                <Fragment key={route.path}>
                  <ListItemButton
                    ref={index === activeIndex ? activeRouteRef : null}
                    selected={index === activeIndex}
                    onClick={() => handleSelect(route)}
                    sx={{ pr: 1.5 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" noWrap>
                            <HighlightedText value={route.title} query={query} />
                          </Typography>
                          {route.requiresOrderNumber && <Chip label="Order #" size="small" />}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          <HighlightedText value={route.path} query={query} />
                        </Typography>
                      }
                    />
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 0.75,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: index === activeIndex ? "primary.main" : "text.disabled",
                        bgcolor: index === activeIndex ? "action.selected" : "transparent",
                        flexShrink: 0
                      }}
                    >
                      <Iconify icon="material-symbols:keyboard-return-rounded" width={18} />
                    </Box>
                  </ListItemButton>
                </Fragment>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      <OrderNumberRouteDialog
        open={Boolean(routeAwaitingOrderNumber)}
        title={routeAwaitingOrderNumber?.title}
        onClose={handleOrderNumberDialogClose}
        onSubmit={handleOrderNumberSubmit}
      />
    </>
  );
}
